import React, { useState, useEffect } from "react";
import {
  safeScrollToTop,
  shouldSkipAutoScroll,
  blockAutoScroll,
} from "../utils/scrollGuard";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_CONFIG } from "../config/api.config";
import { APP_URLS } from "../config/urls.config";
import ModernPhotoGallery from "./ModernPhotoGallery";

const RestaurantDetail = ({
  restaurant,
  user,
  onBack,
  requestedGuests = 0,
}) => {
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restaurantPhotos, setRestaurantPhotos] = useState([]);
  const [newRestaurantPhotoUrl, setNewRestaurantPhotoUrl] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [selectedArea, setSelectedArea] = useState("All");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const defaultRestaurantPhotos = [
    "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
    "https://images.unsplash.com/photo-1552566626-52f8b828add9?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
    "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
    "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
    "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
  ];

  // Combine real and default photos to ensure a beautiful grid (minimum 5)
  const galleryImages = (restaurantPhotos && restaurantPhotos.length > 0)
    ? [...restaurantPhotos.filter(p => p && p.photoUrl).map(p => p.photoUrl), ...defaultRestaurantPhotos].slice(0, Math.max(5, restaurantPhotos.length))
    : defaultRestaurantPhotos;

  // Scroll to top when component mounts or restaurant changes
  useEffect(() => {
    safeScrollToTop();
    try {
      if (!shouldSkipAutoScroll()) {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
    } catch (e) { }
  }, [restaurant.restaurantId]);

  // Fetch restaurant photos
  useEffect(() => {
    axios
      .get(
        `${APP_URLS.API}/restaurant-photos/${restaurant.restaurantId}`,
      )
      .then((res) => {
        if (Array.isArray(res.data)) {
          setRestaurantPhotos(res.data.slice(0, 40));
        } else {
          setRestaurantPhotos([]);
        }
      })
      .catch(() => setRestaurantPhotos([]));
  }, [restaurant.restaurantId]);

  // Fetch tables only (no table photos)
  useEffect(() => {
    axios
      .get(
        `${APP_URLS.API}/restaurants/${restaurant.restaurantId}/tables`,
      )
      .then((res) => {
        setTables(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setTables([]));
  }, [restaurant.restaurantId]);

  const filteredTables = Array.isArray(tables) ? tables.filter(table => {
    if (!table || typeof table !== "object" || typeof table.tableId === "undefined") return false;
    if (requestedGuests && table.seatingCapacity < requestedGuests) return false;
    if (selectedArea !== "All") {
      const tArea = table.area_type || table.areaType || ['Open Area', 'Indoor AC', 'Outdoor'][table.tableNumber % 3];
      return tArea === selectedArea;
    }
    return true;
  }) : [];

  // Handlers
  const handleAddRestaurantPhoto = async () => {
    if (!newRestaurantPhotoUrl || restaurantPhotos.length >= 40) return;
    await axios.post(`${APP_URLS.API}/restaurant-photos`, {
      restaurantId: restaurant.restaurantId,
      photoUrl: newRestaurantPhotoUrl,
    });
    setNewRestaurantPhotoUrl("");
    const res = await axios.get(
      `${API_CONFIG.BASE_URL}/restaurant-photos/${restaurant.restaurantId}`,
    );
    setRestaurantPhotos(Array.isArray(res.data) ? res.data.slice(0, 40) : []);
  };

  const handleTableBooking = (table) => {
    // Navigate to booking page instead of showing modal
    try {
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(
          "restaurantsScrollY",
          String(window.scrollY || 0),
        );
      }
    } catch (e) { }
    try {
      blockAutoScroll(5000);
    } catch (e) { }
    try {
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(
          "focusRestaurantId",
          String(restaurant.restaurantId),
        );
      }
    } catch (e) { }
    navigate(
      `/restaurant-booking/${restaurant.restaurantId}/${table.tableId}`,
      {
        state: { restaurant, table },
      },
    );
  };

  return (
    <div
      className="restaurant-detail"
      style={{
        fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        background: "#FFFFFF",
        minHeight: "100vh",
        paddingTop: isMobile ? "70px" : "90px",
      }}
    >
      {/* Header Section - Centered Buttons */}
      <div
        style={{
          padding: isMobile ? "10px 15px" : "20px",
          maxWidth: "1400px",
          margin: "0 auto",
          position: "relative",
          zIndex: 10,
        }}
      >
        {/* Both Buttons - Centered & Close Together */}
        <div
          style={{
            display: "flex",
            justifyContent: isMobile ? "space-between" : "center",
            alignItems: "center",
            gap: isMobile ? "8px" : "12px",
            flexWrap: "nowrap",
          }}
        >
          <button
            onClick={onBack}
            style={{
              background: "linear-gradient(135deg, #EE2E24 0%, #C62828 100%)",
              color: "white",
              border: "none",
              padding: isMobile ? "8px 14px" : "10px 24px",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: isMobile ? "12px" : "15px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: "0 2px 8px rgba(238, 46, 36, 0.2)",
              display: "inline-flex",
              alignItems: "center",
              gap: isMobile ? "4px" : "8px",
              whiteSpace: "nowrap",
            }}
          >
            {isMobile ? "← Back" : "← Back to Restaurants"}
          </button>

          <button
            onClick={() =>
              window.open(
                `https://trivanamerchant.com/menu/${restaurant.restaurantId}`,
                "_blank",
              )
            }
            style={{
              background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
              color: "white",
              border: "none",
              padding: isMobile ? "8px 16px" : "10px 28px",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: isMobile ? "12px" : "15px",
              fontWeight: "600",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: "0 2px 10px rgba(245, 158, 11, 0.25)",
              display: "inline-flex",
              alignItems: "center",
              gap: isMobile ? "4px" : "8px",
              whiteSpace: "nowrap",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            <span style={{ fontSize: isMobile ? "14px" : "18px" }}>📖</span>
            {isMobile ? "View Menu" : "View Menu"}
          </button>
        </div>
      </div>

      {/* Restaurant Photos Gallery - Premium Grid Layout */}
      <div style={{ maxWidth: "1140px", margin: "0 auto 30px auto", padding: isMobile ? "0 10px" : "0 20px" }}>
        <h4
          style={{
            fontSize: isMobile ? "18px" : "22px",
            marginBottom: "12px",
            textAlign: "left",
            fontWeight: "900",
            color: "#1a1a1a",
            letterSpacing: "-0.5px",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}
        >
          <span style={{ color: "#EE2E24" }}>🍽️</span> Restaurant Photos
        </h4>

        <div
          style={{
            borderRadius: isMobile ? "12px" : "16px",
            overflow: "hidden",
            boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
            border: "1px solid rgba(0,0,0,0.05)"
          }}
        >
          <ModernPhotoGallery
            images={galleryImages}
            title=""
          />
        </div>
      </div>

      {/* Tables Section - Premium Background Wrapper */}
      <section style={{ background: "#f9fafb", padding: isMobile ? "30px 0" : "60px 0", borderTop: "1px solid #eee" }}>
        {/* Tables Section Header & Area Tabs */}
        <div style={{ textAlign: "center", marginBottom: isMobile ? "25px" : "40px", padding: "0 15px" }}>
          <h2
            style={{
              fontSize: isMobile ? "24px" : "36px",
              fontWeight: "900",
              color: "#1a1a1a",
              marginBottom: "8px",
              letterSpacing: "-0.8px"
            }}
          >
            Select Your Table
          </h2>
          <p style={{ color: "#6b7280", fontSize: isMobile ? "14px" : "17px", marginBottom: "20px", fontWeight: "500" }}>
            Choose from our premium seating arrangements
          </p>

          {/* Filter Tabs */}
          <div style={{
            display: "flex",
            justifyContent: isMobile ? "flex-start" : "center",
            gap: isMobile ? "8px" : "12px",
            flexWrap: isMobile ? "nowrap" : "wrap",
            marginBottom: isMobile ? "20px" : "30px",
            overflowX: isMobile ? "auto" : "visible",
            paddingBottom: isMobile ? "10px" : "0",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            msOverflowStyle: "none"
          }}>
            {["All", "Indoor AC", "Open Area", "Outdoor"].map(area => {
              const count = area === "All" ? tables.length : tables.filter(t => (t.area_type || t.areaType || ['Open Area', 'Indoor AC', 'Outdoor'][t.tableNumber % 3]) === area).length;
              return (
                <button
                  key={area}
                  onClick={() => setSelectedArea(area)}
                  style={{
                    padding: isMobile ? "8px 18px" : "12px 28px",
                    borderRadius: "30px",
                    border: "none",
                    fontSize: isMobile ? "0.8rem" : "0.95rem",
                    fontWeight: "700",
                    cursor: "pointer",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    background: selectedArea === area ? "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)" : "#fff",
                    color: selectedArea === area ? "#fff" : "#4b5563",
                    boxShadow: selectedArea === area ? "0 8px 25px rgba(238, 46, 36, 0.25)" : "0 2px 10px rgba(0,0,0,0.03)",
                    transform: selectedArea === area && !isMobile ? "translateY(-3px)" : "none",
                    border: selectedArea === area ? "none" : "1px solid #eee",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    whiteSpace: "nowrap",
                    flexShrink: 0
                  }}
                >
                  {area}
                  <span style={{
                    fontSize: "0.7rem",
                    opacity: 0.8,
                    background: selectedArea === area ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.05)",
                    padding: "2px 6px",
                    borderRadius: "20px"
                  }}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Matching Badge - Enterprise Detail */}
          <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            background: "rgba(238, 46, 36, 0.05)",
            color: "#EE2E24",
            padding: "8px 20px",
            borderRadius: "50px",
            fontSize: "0.85rem",
            fontWeight: "800",
            border: "1px solid rgba(238, 46, 36, 0.1)"
          }}>
            <span style={{ fontSize: "1rem" }}>⚡</span>
            {filteredTables.length} Tables Available in {selectedArea} Category
          </div>
        </div>

        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "100px 0",
            }}
          >
            <div className="res-loading-spinner" style={{ width: "50px", height: "50px", border: "5px solid #f3f3f3", borderTop: "5px solid #EE2E24", borderRadius: "50%" }}></div>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: isMobile ? "20px" : "35px",
              padding: isMobile ? "10px 10px 40px 10px" : "20px 20px 80px 20px",
              maxWidth: "1350px",
              margin: "0 auto",
            }}
          >
            {filteredTables.length > 0 ? (
              filteredTables.map((table) => (
                <div
                  key={table.tableId}
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "24px",
                    border: "1px solid rgba(0,0,0,0.06)",
                    overflow: "hidden",
                    transition: "all 0.6s cubic-bezier(0.165, 0.84, 0.44, 1)",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.04)",
                    width: "380px",
                    flex: "0 1 380px",
                    position: "relative",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "perspective(1000px) rotateX(2deg) translateY(-8px) scale(1.02)";
                    e.currentTarget.style.boxShadow = "0 25px 50px rgba(0,0,0,0.15), 0 0 0 1px rgba(238,46,36,0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "perspective(1000px) rotateX(0deg) translateY(0) scale(1)";
                    e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.08)";
                  }}
                >
                  {/* Decorative 3D Lighting layer */}
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "100%", background: "linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 50%)", pointerEvents: "none", zIndex: 10 }}></div>

                  {/* Trivana-Style Image Grid */}
                  <div
                    style={{
                      position: "relative",
                      height: isMobile ? "160px" : "200px",
                      display: "grid",
                      gridTemplateColumns: "70% 30%",
                      gap: "2px",
                      backgroundColor: "#fff",
                    }}
                  >
                    {/* Main Image - 70% */}
                    <div style={{ position: "relative", overflow: "hidden" }}>
                      <img
                        src={(() => {
                          const tableImages = [
                            "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
                            "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
                            "https://images.unsplash.com/photo-1514933651103-005eec06c04b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
                            "https://images.unsplash.com/photo-1481833761820-0509d3217039?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
                            "https://images.unsplash.com/photo-1552566651-75f7c2b3038c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
                            "https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
                            "https://images.unsplash.com/photo-1559329007-40df8aa4539c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
                            "https://images.unsplash.com/photo-1551218808-94e220e084d2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
                          ];
                          return tableImages[
                            (table.tableNumber - 1) % tableImages.length
                          ];
                        })()}
                        alt={`Table ${table.tableNumber}`}
                        style={{
                          width: "100%",
                          height: isMobile ? "160px" : "200px",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    </div>

                    {/* Thumbnail Images - 30% (3 stacked) */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                      }}
                    >
                      {[0, 1, 2].map((idx) => (
                        <div key={idx} style={{ flex: 1, overflow: "hidden" }}>
                          <img
                            src={(() => {
                              const thumbImages = [
                                "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
                                "https://images.unsplash.com/photo-1466978913421-dad2ebd01d17?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
                                "https://images.unsplash.com/photo-1424847651672-bf20a4b0982b?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
                              ];
                              return thumbImages[idx];
                            })()}
                            alt={`Thumbnail ${idx + 1}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div
                    style={{
                      padding: "12px 14px",
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                    }}
                  >
                    {/* Table Name */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                      <h3
                        style={{
                          fontSize: "1.2rem",
                          fontWeight: "900",
                          color: "#1A1A1A",
                          margin: 0,
                          letterSpacing: "-0.5px"
                        }}
                      >
                        Table T{table.tableNumber}
                      </h3>
                      <span style={{
                        background: "#fff5f5",
                        color: "#EE2E24",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        fontSize: "0.65rem",
                        fontWeight: "800",
                        textTransform: "uppercase",
                        border: "1px solid #ffeaea"
                      }}>
                        {table.area_type || table.areaType || ['Open Area', 'Indoor AC', 'Outdoor'][table.tableNumber % 3]}
                      </span>
                    </div>

                    {/* Location/Restaurant Name */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        marginBottom: "12px",
                      }}
                    >
                      <span style={{ fontSize: "0.85rem", color: "#EE2E24" }}>
                        📍
                      </span>
                      <span
                        style={{
                          fontSize: "0.8rem",
                          color: "#6B7280",
                          fontWeight: "500",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {restaurant.name}
                      </span>
                    </div>

                    {/* Amenities */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginBottom: "6px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span style={{ fontSize: "0.7rem", color: "#4B5563" }}>
                        ✓ Premium Seating
                      </span>
                      <span style={{ fontSize: "0.7rem", color: "#4B5563" }}>
                        🍴 Table Service
                      </span>
                    </div>

                    {/* Capacity */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        marginBottom: "10px",
                      }}
                    >
                      <span style={{ fontSize: "0.7rem", color: "#6B7280" }}>
                        👥 Capacity:{" "}
                      </span>
                      <span
                        style={{
                          fontSize: "0.75rem",
                          fontWeight: "600",
                          color: "#1A1A1A",
                        }}
                      >
                        {table.seatingCapacity} persons
                      </span>
                    </div>

                    {/* Divider */}
                    <div
                      style={{
                        height: "1px",
                        background: "#E5E7EB",
                        margin: "10px 0",
                      }}
                    ></div>

                    {/* Price Section - Per Table (Not Per Person) */}
                    <div style={{ marginTop: "auto" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: "6px",
                          marginBottom: "4px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "1.5rem",
                            fontWeight: "700",
                            color: "#1A1A1A",
                          }}
                        >
                          ₹{table.pricePerPerson || 100}
                        </span>
                        <span
                          style={{
                            fontSize: "0.85rem",
                            color: "#9CA3AF",
                            textDecoration: "line-through",
                          }}
                        >
                          ₹{Math.round((table.pricePerPerson || 100) * 1.67)}
                        </span>
                        <span
                          style={{
                            fontSize: "0.75rem",
                            color: "#EE2E24",
                            fontWeight: "700",
                            fontFamily: "'Inter', sans-serif",
                          }}
                        >
                          40% off
                        </span>
                      </div>

                      {/* Buttons - Trivana Style */}
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                        }}
                      >
                        {!isMobile && (
                          <button
                            style={{
                              flex: 1,
                              padding: "10px 12px",
                              borderRadius: "6px",
                              fontSize: "0.85rem",
                              fontWeight: "600",
                              border: "1px solid #E5E7EB",
                              background: "#FFFFFF",
                              color: "#1A1A1A",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = "#F9FAFB";
                              e.target.style.borderColor = "#D1D5DB";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = "#FFFFFF";
                              e.target.style.borderColor = "#E5E7EB";
                            }}
                          >
                            View Details
                          </button>
                        )}
                        <button
                          style={{
                            flex: 1,
                            padding: isMobile ? "12px 14px" : "10px 12px",
                            borderRadius: "10px",
                            fontSize: isMobile ? "0.9rem" : "0.85rem",
                            fontWeight: "800",
                            border: "none",
                            background: table.isAvailable
                              ? "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)"
                              : "#9CA3AF",
                            color: "#FFFFFF",
                            cursor: table.isAvailable
                              ? "pointer"
                              : "not-allowed",
                            transition: "all 0.2s ease",
                            opacity: table.isAvailable ? 1 : 0.7,
                            fontFamily: "'Inter', sans-serif",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            boxShadow: table.isAvailable
                              ? "0 4px 12px rgba(238, 46, 36, 0.25)"
                              : "none",
                          }}
                          onClick={() =>
                            table.isAvailable && handleTableBooking(table)
                          }
                          disabled={!table.isAvailable}
                        >
                          {table.isAvailable ? "Book Now" : "Occupied"}
                        </button>
                      </div>

                      {/* Popularity Badge */}
                      {table.isAvailable && (
                        <div
                          style={{
                            marginTop: "10px",
                            padding: "6px 10px",
                            background: "#FEF2F2",
                            borderRadius: "4px",
                            fontSize: "0.7rem",
                            color: "#EE2E24",
                            fontWeight: "500",
                            textAlign: "center",
                          }}
                        >
                          🔥 450+ people booked in last 6 months
                        </div>
                      )}
                    </div>
                  </div>
                </div >
              ))
            ) : (
              <div style={{
                width: "100%",
                padding: "80px 20px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
                borderRadius: "40px",
                margin: "20px 0"
              }}>
                <div style={{
                  maxWidth: "600px",
                  width: "100%",
                  background: "#fff",
                  padding: "80px 40px",
                  borderRadius: "40px",
                  textAlign: "center",
                  boxShadow: "0 25px 70px rgba(0,0,0,0.04)",
                  border: "1px solid #f0f0f0",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  position: "relative",
                  overflow: "hidden"
                }}>
                  {/* Decorative Elements */}
                  <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "4px", background: "linear-gradient(90deg, transparent, #EE2E24, transparent)", opacity: 0.4 }}></div>
                  <div style={{ position: "absolute", top: "-50px", right: "-50px", width: "150px", height: "150px", background: "rgba(238, 46, 36, 0.02)", borderRadius: "50%", blur: "40px" }}></div>

                  <div style={{
                    width: "100px",
                    height: "100px",
                    background: "rgba(238, 46, 36, 0.03)",
                    borderRadius: "35px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "42px",
                    marginBottom: "30px",
                    border: "1px solid rgba(238, 46, 36, 0.08)",
                    boxShadow: "inset 0 2px 10px rgba(238, 46, 36, 0.02)"
                  }}>
                    🍽️
                  </div>

                  <h3 style={{
                    fontSize: "32px",
                    fontWeight: "900",
                    color: "#1a1a1a",
                    margin: "0 0 15px 0",
                    letterSpacing: "-1px",
                    lineHeight: "1.2"
                  }}>
                    The Perfect Spot is Tucked Away
                  </h3>

                  <p style={{
                    fontSize: "1.1rem",
                    color: "#6b7280",
                    lineHeight: "1.8",
                    margin: "0 0 40px 0",
                    maxWidth: "400px",
                    fontWeight: "500"
                  }}>
                    All tables in the <span style={{ color: "#EE2E24", fontWeight: "800", background: "rgba(238,46,36,0.05)", padding: "2px 10px", borderRadius: "8px" }}>{selectedArea !== "All" ? selectedArea : "Premium"}</span> category are currently occupied.
                  </p>

                  {selectedArea !== "All" && (
                    <button
                      onClick={() => setSelectedArea("All")}
                      style={{
                        padding: "18px 45px",
                        borderRadius: "20px",
                        border: "none",
                        background: "linear-gradient(135deg, #1a1a1a 0%, #000000 100%)",
                        color: "#fff",
                        fontSize: "0.95rem",
                        fontWeight: "800",
                        cursor: "pointer",
                        transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
                        textTransform: "uppercase",
                        letterSpacing: "1px"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-4px) scale(1.02)";
                        e.currentTarget.style.boxShadow = "0 25px 50px rgba(0,0,0,0.3)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0) scale(1)";
                        e.currentTarget.style.boxShadow = "0 20px 40px rgba(0,0,0,0.2)";
                      }}
                    >
                      Explore All Categories
                    </button>
                  )}

                  <div style={{ marginTop: "40px", display: "flex", gap: "8px" }}>
                    {[1, 2, 3].map(i => (
                      <div key={i} style={{ width: i === 1 ? "30px" : "8px", height: "8px", borderRadius: "10px", background: i === 1 ? "#EE2E24" : "#f0f0f0" }}></div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};
export default RestaurantDetail;
